import { select } from 'd3-selection';
import { line } from 'd3-shape';
import { useEffect, useRef } from 'react';
import { convertBeatTypeNumberToColorV2, convertBeatTypeNumberToText } from '@/shared/ui';
import {
  calculateConfig,
  calculatePixelSamples,
  calculateSamples,
  CONFIG_ECG,
} from '@/entities/ecg';
import type { ThumbnailHRSProps } from './thumbnailHRS';

/**
 * Renders a small ECG strip thumbnail for the HR Summary tab into an SVG element. Picks the requested channels out of the ECG data, computes the display config from gain, speed, size, high-pass filter and sampling rate, converts samples to pixels, then clears the SVG and appends one path per channel plus a coloured beat-type label at each beat marker; it redraws whenever the data or display settings change.
 *
 * @param props - ECG data, channels, gain, speed, width, height, high-pass filter, beat markers and beat statuses.
 * @returns An object carrying canvasRef, the ref to attach to the target SVG element.
 */
const useThumbnailHRS = ({
  ecgData,
  channels = [2],
  gain,
  speed,
  width,
  height,
  highPass,
  beatMarkers = [],
  hesBeatStatus = [],
}: Pick<
  ThumbnailHRSProps,
  | 'ecgData'
  | 'channels'
  | 'gain'
  | 'speed'
  | 'width'
  | 'height'
  | 'highPass'
  | 'beatMarkers'
  | 'hesBeatStatus'
>) => {
  const canvasRef = useRef<SVGSVGElement>(null);

  const drawStrip = ({
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels = [],
  }: {
    ecgData: number[][];
    beatMarkers: number[];
    hesBeatStatus: number[];
    channels?: number[];
  }) => {
    if (channels.length === 0) {
      return;
    }
    const countChannels = ecgData.length;
    const formatChannels = channels.map((x) => x - 1);
    const drawEcgData: number[][] = [];
    if (countChannels > 1) {
      formatChannels.forEach((x) => {
        drawEcgData.push(ecgData[x]);
      });
    } else {
      drawEcgData.push(ecgData[0]);
    }
    let formatHeight = height;
    if (drawEcgData.length > 1) {
      formatHeight -= 10;
    }
    // calculateConfig's JSDoc param type (BeatHR/helper, not yet converted) also
    // declares a pHFF field this call site never passed.
    const config = calculateConfig({
      pGain: gain,
      pSpeed: speed,
      width,
      height: formatHeight,
      adcGain: CONFIG_ECG.gain,
      ecgData: drawEcgData,
      samplingRate: CONFIG_ECG.samplingFrequency,
      pLFF: highPass,
    } as any);
    const samples = calculateSamples(drawEcgData, config);
    const pixelSamples = calculatePixelSamples(samples, config);
    const { indexToPix } = config;
    const pixelBeats = beatMarkers
      .map((beatMarker) => Math.floor(beatMarker * indexToPix))
      .filter((pixelBeat) => !Number.isNaN(pixelBeat)); // Ensure no NaN values

    if (canvasRef.current) {
      const svg = select(canvasRef.current);
      svg.selectAll('*').remove();

      // Draw grid lines
      // const gridSpacing = 10; // Adjust spacing as needed
      // for (let x = 0; x < width; x += gridSpacing) {
      //   svg.append('line')
      //     .attr('x1', x)
      //     .attr('y1', 0)
      //     .attr('x2', x)
      //     .attr('y2', height)
      //     .attr('stroke', '#d4e6fe')
      //     .attr('stroke-width', '0.4px');
      // }
      // for (let y = 0; y < height; y += gridSpacing) {
      //   svg.append('line')
      //     .attr('x1', 0)
      //     .attr('y1', y)
      //     .attr('x2', width)
      //     .attr('y2', y)
      //     .attr('stroke', '#d4e6fe')
      //     .attr('stroke-width', '0.4px');
      // }

      const lineFunc = line<{ x: number; y: number }>()
        .x((d) => d.x)
        .y((d) => d.y + 15);
      pixelSamples.forEach((displayedSamples: any) => {
        if (
          displayedSamples.every((point: any) => !Number.isNaN(point.x) && !Number.isNaN(point.y))
        ) {
          // Validate points
          svg
            .append('path')
            .attr('d', lineFunc(displayedSamples))
            .attr('stroke', '#1D2F47')
            .attr('stroke-width', '0.75px')
            .attr('fill', 'none');
        }
      });

      pixelBeats.forEach((pixelBeat, index) => {
        if (!Number.isNaN(pixelBeat)) {
          // Ensure pixelBeat is valid
          svg
            .append('text')
            .attr('x', pixelBeat)
            .attr('y', 10)
            .attr('text-anchor', 'middle')
            .style('font-size', '10px')
            .style('font-weight', '600')
            .style('font-family', 'Lato')
            .style('pointer-events', 'none')
            .style('fill', convertBeatTypeNumberToColorV2(hesBeatStatus[index]))
            .text(convertBeatTypeNumberToText(hesBeatStatus[index]));
        }
      });
    }
  };

  useEffect(() => {
    if (ecgData?.length > 0) {
      drawStrip({
        ecgData,
        beatMarkers,
        hesBeatStatus,
        channels,
      });
    }
  }, [
    ecgData,
    JSON.stringify(channels),
    gain,
    highPass,
    width,
    height,
    beatMarkers,
    hesBeatStatus,
  ]);

  return { canvasRef };
};

export default useThumbnailHRS;
